import { ReactNode, useState } from "react";
import { create } from "zustand";

import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// components

import { Button } from "@/components/ui/button";
import { SvgSpinner } from "@/components/SvgIcon";

const ConfirmDialog = () => {
	const {
		title,
		message,
		onSubmit,
		close,
		open,
		confirmText = "Confirm",
		cancelText = "Back",
	} = useConfirmDialogStore();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleConfirm = async () => {
		setIsLoading(true);
		try {
			await onSubmit(true);
			await new Promise((resolve) => setTimeout(resolve, 1000));
			setIsLoading(false);
			close();
		} catch (err) {
			setIsLoading(false);
		}
	};

	return (
		<AlertDialog open={open} onOpenChange={close}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{message}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter className="flex justify-center items-end space-x-2">
					<div className="flex justify-center items-end space-x-2">
						<Button
							variant="default"
							className="bg-white border border-opus-gray-800 text-opus-gray-800 rounded-full px-8 min-w-28 hover:text-white"
							disabled={isLoading}
							onClick={close}>
							{cancelText}
						</Button>
						<Button
							variant="default"
							className="bg-opus-blue-100 border border-opus-blue-100 text-white rounded-full px-8"
							disabled={isLoading}
							onClick={() => handleConfirm()}>
							{isLoading && <SvgSpinner className="w-5 h-5 mr-2" />}{" "}
							{confirmText}
						</Button>
					</div>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

interface IConfirm {
	title?: string;
	message: ReactNode;
	variant?: "error" | "success" | "warning";
	onSubmit: (x: boolean) => Promise<any> | void;
	confirmVariant?: "warning";
	confirmText?: string;
	cancelText?: string;
}

const useConfirmDialogStore = create<
	IConfirm & { close: () => void; open: boolean }
>((set) => ({
	title: "",
	message: "",
	open: false,
	variant: "error",
	onSubmit: (open: boolean) => set({ open }),
	confirmVariant: "warning",
	confirmText: "",
	cancelText: "",
	close: () => set({ open: false }),
}));

export const confirmDialog = ({
	title,
	message,
	variant = "error",
	confirmVariant = "warning",
	confirmText = "Confirm",
	cancelText = "Back",
	onSubmit,
}: IConfirm) => {
	useConfirmDialogStore.setState({
		title,
		open: true,
		variant,
		message,
		confirmVariant,
		confirmText,
		cancelText,
		onSubmit,
	});
};

export default ConfirmDialog;
